import { moNum } from "@/lib/moNum";
import { sequenceMos, buildDesignMap, moWater, moCap, sequenceWithRdd } from "@/lib/productionSequence";
import { Switch } from "@/components/ui/switch";
import { useState, useEffect, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ProductionReportForm from "@/components/ProductionReportForm";
import ProjectionsSection from "@/components/ProjectionsSection";
import MOCreator from "@/pages/ManufacturingOrders";
import { SODialog } from "@/pages/SalesOrders";
import MODetailModal from "@/components/MODetailModal";
import EditMODialog from "@/components/EditMODialog";
import { printProductionOrder } from "@/lib/productionOrderPrint";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CalendarDays, Loader2, Eye, Pencil, Trash2, ArrowLeft, ChevronDown, Plus, ListTree, ArrowDownRight, ArrowUpRight, Search, X, RotateCcw, Timer, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const OZ_TO_LABEL = { "12": "12oz | 355mL", "16.9": "16.9oz | 500mL", "23.7": "23.7oz | 700mL", "33.8": "33.8oz | 1 L" };
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtDate = (s) => {
  if (!s) return "No date";
  const d = new Date(s + "T00:00:00");
  return isNaN(d) ? s : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

// Flat summary of daily production reports (one row per report line).
// Production stations shown on the daily reports summary (count + losses/downtime).
const STATIONS = [
  { key: "blow_mold", label: "Blow Mold", prev: "start" },
  { key: "filler", label: "Filler", prev: "blow_mold" },
  { key: "labeler", label: "Labeler", prev: "filler" },
  { key: "packer", label: "Packer", prev: "labeler" },
  { key: "palletizer", label: "Palletizer", prev: "packer" },
];

function StationCell({ li, station, testId }) {
  const counts = li.production_counts || {};
  const dt = li.mechanical_downtime || {};
  const n = (v) => (v == null || v === "" || Number.isNaN(Number(v)) ? null : Number(v));
  const count = n(counts[station.key]);
  const prev = n(counts[station.prev]);
  const loss = count != null && prev != null ? Math.round((prev - count) * 100) / 100 : null;
  const down = n(dt[station.key]);
  return (
    <td className="border-l border-slate-100 px-3 py-2 text-right align-top" data-testid={testId}>
      <span className="tabular-nums font-medium text-slate-800">{count == null ? "—" : count.toLocaleString()}</span>
      {(loss != null || down != null) && (
        <p className="mt-0.5 whitespace-nowrap text-[10px] leading-tight tabular-nums text-slate-400">
          {loss != null && <span className={loss > 0 ? "text-red-500" : ""}>{loss > 0 ? `−${loss.toLocaleString()}` : loss < 0 ? `+${Math.abs(loss).toLocaleString()}` : "0"} loss</span>}
          {loss != null && down != null && " · "}
          {down != null && <span>{down.toLocaleString()}m DT</span>}
        </p>
      )}
    </td>
  );
}

function DailyReportsSummary({ reports, mos, onView, onEdit, onDelete, onNew }) {
  const [shown, setShown] = useState(25);
  const moById = new Map(mos.map((m) => [m.id, m]));
  const rows = [];
  reports.forEach((r) => {
    const items = (r.line_items || []).length ? r.line_items : [{}];
    items.forEach((li, i) => rows.push({ r, li, i }));
  });
  rows.sort((a, b) => (b.r.date || "").localeCompare(a.r.date || "") || (b.r.created_at || "").localeCompare(a.r.created_at || ""));
  const visible = rows.slice(0, shown);
  const fmtB = (n) => (n == null || n === "" ? "—" : Math.round(Number(n)).toLocaleString());
  const producedOf = (li) => {
    const show = (v) => (v == null || Number.isNaN(v) ? "—" : Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 1 }));
    const bottles = Number(li.stockable_bottles || 0) || null;
    const csRaw = li.stockable_cases;
    const totalCases = csRaw !== null && csRaw !== undefined && csRaw !== "" && !Number.isNaN(Number(csRaw))
      ? Number(csRaw)
      : (bottles && li.pack_size ? bottles / Number(li.pack_size) : null);
    const cpp = Number(li.cases_per_pallet) || null;
    let pallets = null;
    let remCases = totalCases;
    if (totalCases != null && cpp) {
      pallets = Math.floor(totalCases / cpp);
      remCases = Math.round((totalCases - pallets * cpp) * 100) / 100;
    }
    return { pallets: show(pallets), cases: show(remCases), bottles: show(bottles) };
  };
  return (
    <div className="mt-10" data-testid="daily-reports-section">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#002FA7]" />
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Daily Production Reports</h2>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500">{reports.length}</span>
        </div>
        <Button onClick={onNew} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="new-production-report">
          <Plus className="mr-1 h-4 w-4" /> New Production Report
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center border border-dashed border-slate-300 px-6 py-12 text-center" data-testid="daily-reports-empty">
          <FileText className="h-9 w-9 text-slate-300" strokeWidth={1.5} />
          <p className="mt-3 text-sm text-slate-500">No production reports yet. End-of-day numbers entered here update finished goods automatically.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto border border-slate-200" data-testid="daily-reports-table">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                  <th rowSpan={2} className="px-3 py-2">DOP</th>
                  <th rowSpan={2} className="border-l border-slate-200 px-3 py-2">Shift</th>
                  <th rowSpan={2} className="border-l border-slate-200 px-3 py-2">Size — Design</th>
                  <th rowSpan={2} className="border-l border-slate-200 px-3 py-2 text-right">Start Amount</th>
                  {STATIONS.map((s) => (
                    <th key={s.key} rowSpan={2} className="border-l border-slate-200 px-3 py-2 text-right">{s.label}</th>
                  ))}
                  <th colSpan={2} className="border-b border-l border-slate-200 px-3 py-1.5 text-center">Total Produced</th>
                  <th rowSpan={2} className="border-l border-slate-200 px-3 py-2 text-right">Bottles</th>
                  <th rowSpan={2} className="border-l border-slate-200 px-3 py-2">MO #</th>
                  <th rowSpan={2} className="w-28 border-l border-slate-200 px-2 py-2" />
                </tr>
                <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                  <th className="border-l border-slate-200 px-3 py-1.5 text-right">Pallets</th>
                  <th className="border-l border-slate-200 px-3 py-1.5 text-right">Cases</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(({ r, li, i }) => {
                  const mo = r.mwo_id ? moById.get(r.mwo_id) : null;
                  const prod = producedOf(li);
                  return (
                    <tr key={`${r.id}-${i}`} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`daily-report-row-${r.id}-${i}`}>
                      <td className="whitespace-nowrap px-3 py-2.5 font-medium text-slate-800">{fmtDate(r.date)}</td>
                      <td className="border-l border-slate-100 px-3 py-2.5 text-slate-500">{r.shift || "—"}</td>
                      <td className="border-l border-slate-100 px-3 py-2.5">
                        <span className="whitespace-nowrap text-slate-600">{li.bottle_size || "—"}</span>
                        {li.label_name && <span className="text-slate-800"> — {li.label_name}</span>}
                      </td>
                      <td className="border-l border-slate-100 px-3 py-2.5 text-right tabular-nums text-slate-600" data-testid={`daily-report-start-${r.id}-${i}`}>{fmtB(li.production_counts?.start)}</td>
                      {STATIONS.map((s) => (
                        <StationCell key={s.key} li={li} station={s} testId={`daily-report-${s.key}-${r.id}-${i}`} />
                      ))}
                      <td className="border-l border-slate-100 px-3 py-2.5 text-right tabular-nums text-slate-700" data-testid={`daily-report-pallets-${r.id}-${i}`}>{prod.pallets}</td>
                      <td className="border-l border-slate-100 px-3 py-2.5 text-right tabular-nums text-slate-700" data-testid={`daily-report-cases-${r.id}-${i}`}>{prod.cases}</td>
                      <td className="border-l border-slate-100 px-3 py-2.5 text-right tabular-nums font-semibold text-emerald-700" data-testid={`daily-report-stockable-${r.id}-${i}`}>{prod.bottles}</td>
                      <td className="border-l border-slate-100 px-3 py-2.5">
                        {(() => {
                          const fills = (r.auto_allocations || []).filter((a) => a.mwo_number);
                          if (mo) return <span className="font-semibold text-[#002FA7]">{moNum(mo.mwo_number)}</span>;
                          if (fills.length) return (
                            <span className="flex flex-wrap gap-1">
                              {fills.map((a, k) => (
                                <span key={k} className="whitespace-nowrap rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-[#002FA7]" title={`${Number(a.bottles).toLocaleString()} bottles counted toward this MO`} data-testid={`report-filled-${r.id}-${k}`}>
                                  {moNum(a.mwo_number)}
                                </span>
                              ))}
                            </span>
                          );
                          return <span className="text-slate-400">—</span>;
                        })()}
                      </td>
                      <td className="border-l border-slate-100 px-2 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-[#002FA7]" title="View report" onClick={() => onView(r.id)} data-testid={`daily-report-view-${r.id}-${i}`}><Eye className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-[#002FA7]" title="Edit report" onClick={() => onEdit(r.id)} data-testid={`daily-report-edit-${r.id}-${i}`}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-600" title="Delete report" onClick={() => onDelete(r)} data-testid={`daily-report-delete-${r.id}-${i}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rows.length > shown && (
            <button onClick={() => setShown((s) => s + 25)} className="mt-2 text-sm font-semibold text-[#002FA7] hover:underline" data-testid="daily-reports-show-more">
              Show more ({rows.length - shown} more)
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Inline editor for a scheduled MO production date.
function ScheduleDateCell({ m, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(m.production_date || "");
  const save = async (v) => {
    setEditing(false);
    if (!v || v === m.production_date) return;
    try {
      const { data } = await api.put(`/manufacturing-orders/${m.id}/schedule`, { production_date: v });
      toast.success(`${moNum(m.mwo_number)} rescheduled to ${fmtDate(v)}`);
      if (data?.synced_so_number) toast.info(`${data.synced_so_number} ship date updated to match`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => { setVal(m.production_date || ""); setEditing(true); }}
        className="group inline-flex items-center gap-1.5 text-slate-600 hover:text-[#002FA7]"
        title="Change scheduled date"
        data-testid={`schedule-date-${m.id}`}
      >
        {fmtDate(m.production_date)}
        <Pencil className="h-3 w-3 text-slate-300 transition-colors group-hover:text-[#002FA7]" />
      </button>
    );
  }
  return (
    <Input
      type="date"
      autoFocus
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => save(val)}
      onKeyDown={(e) => { if (e.key === "Enter") save(val); if (e.key === "Escape") setEditing(false); }}
      className="h-8 w-36 rounded-sm border-slate-300 text-sm"
      data-testid={`schedule-date-input-${m.id}`}
    />
  );
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${ampm}`;
};
const fmtDur = (h) => {
  const total = Math.round((Number(h) || 0) * 60);
  const hrs = Math.floor(total / 60);
  const mins = total % 60;
  if (hrs && mins) return `${hrs}h ${mins}m`;
  if (hrs) return `${hrs}h`;
  return `${mins}m`;
};
const opOccursOn = (op, dayKey) => {
  if (!op.date) return false;
  if (op.recurrence === "daily") return dayKey >= op.date;
  if (op.recurrence === "weekly") {
    return dayKey >= op.date && new Date(dayKey + "T00:00:00").getDay() === new Date(op.date + "T00:00:00").getDay();
  }
  return dayKey === op.date;
};

// Popover to schedule a timed operation (changeover, CIP, ...).
function TimedOpPopover({ mos = [], onCreated }) {
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newHours, setNewHours] = useState("");
  const [newMins, setNewMins] = useState("");
  const [mode, setMode] = useState("date");
  const [moId, setMoId] = useState("");
  const [date, setDate] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [time, setTime] = useState("08:00");
  const [recurrence, setRecurrence] = useState("none");
  const [saving, setSaving] = useState(false);
  const weekday = date ? WEEKDAYS[new Date(date + "T00:00:00").getDay()] : "";
  const linked = mode !== "date";
  const sel = types.find((t) => t.id === typeId) || null;
  const loadTypes = async () => {
    try { setTypes((await api.get("/operation-types")).data); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const onOpenChange = (o) => { setOpen(o); if (o) loadTypes(); };
  const newDuration = (parseFloat(newHours) || 0) + (parseFloat(newMins) || 0) / 60;
  const saveType = async () => {
    try {
      const { data } = await api.post("/operation-types", { name: newName.trim(), duration_hours: newDuration });
      toast.success(`"${data.name}" saved to your operations`);
      setCreating(false); setNewName(""); setNewHours(""); setNewMins("");
      await loadTypes();
      setTypeId(data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const removeType = async () => {
    if (!sel || !window.confirm(`Delete "${sel.name}" from your saved operations? Already-scheduled entries stay on the calendar.`)) return;
    try { await api.delete(`/operation-types/${sel.id}`); setTypeId(""); loadTypes(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const save = async () => {
    if (!sel) return;
    setSaving(true);
    try {
      await api.post("/timed-operations", {
        name: sel.name, duration_hours: sel.duration_hours,
        date: linked ? "" : date, time, recurrence: linked ? "none" : recurrence,
        mo_id: linked ? moId : "", position: linked ? mode : "",
      });
      toast.success(`${sel.name} added to schedule`);
      setOpen(false); setMoId("");
      onCreated?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="rounded-sm border-slate-200 text-slate-600 hover:text-[#002FA7]" data-testid="add-timed-op-button">
          <Timer className="mr-1 h-4 w-4" /> Timed Operation
          <ChevronDown className="ml-1 h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-sm p-3" data-testid="timed-op-panel">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Place Timed Operation</p>
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <Select value={typeId || undefined} onValueChange={setTypeId}>
              <SelectTrigger className="flex-1 rounded-sm" data-testid="timed-op-type"><SelectValue placeholder={types.length ? "Select an operation" : "No saved operations yet"} /></SelectTrigger>
              <SelectContent>
                {types.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name} · {fmtDur(t.duration_hours)}</SelectItem>))}
              </SelectContent>
            </Select>
            {sel && (
              <button type="button" onClick={removeType} className="rounded-sm border border-slate-200 p-2 text-slate-400 hover:text-red-600" title="Delete this saved operation" data-testid="timed-op-type-delete"><Trash2 className="h-3.5 w-3.5" /></button>
            )}
          </div>
          {creating ? (
            <div className="space-y-1.5 rounded-sm border border-dashed border-slate-200 p-2">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Operation name (e.g. CIP)" className="rounded-sm" data-testid="new-op-name" />
              <div className="flex items-center gap-1.5">
                <Input type="text" inputMode="numeric" value={newHours} onChange={(e) => setNewHours(e.target.value.replace(/[^0-9]/g, ""))} placeholder="Hours" className="rounded-sm" data-testid="new-op-hours" />
                <span className="text-xs text-slate-500">h</span>
                <Input type="text" inputMode="numeric" value={newMins} onChange={(e) => setNewMins(e.target.value.replace(/[^0-9]/g, ""))} placeholder="Min" className="rounded-sm" data-testid="new-op-mins" />
                <span className="text-xs text-slate-500">m</span>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" onClick={saveType} disabled={!newName.trim() || !(newDuration > 0)} className="h-8 flex-1 rounded-sm bg-[#002FA7] text-xs text-white hover:bg-[#002080]" data-testid="new-op-save">Save operation</Button>
                <Button size="sm" variant="outline" onClick={() => setCreating(false)} className="h-8 rounded-sm text-xs">Cancel</Button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setCreating(true)} className="text-xs font-semibold text-[#002FA7] hover:underline" data-testid="new-op-toggle">+ New operation</button>
          )}
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="rounded-sm" data-testid="timed-op-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="date">On a fixed date</SelectItem>
              <SelectItem value="before">Before a Manufacturing Order</SelectItem>
              <SelectItem value="after">After a Manufacturing Order</SelectItem>
            </SelectContent>
          </Select>
          {linked ? (
            <Select value={moId || undefined} onValueChange={setMoId}>
              <SelectTrigger className="rounded-sm" data-testid="timed-op-mo"><SelectValue placeholder="Select an MO" /></SelectTrigger>
              <SelectContent>
                {mos.filter((m) => m.status !== "Completed").map((m) => (
                  <SelectItem key={m.id} value={m.id}>{moNum(m.mwo_number)} · {m.product_name}{m.design_name ? ` · ${m.design_name}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="grid grid-cols-2 gap-1.5">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-sm" data-testid="timed-op-date" />
              <Select value={recurrence} onValueChange={setRecurrence}>
                <SelectTrigger className="rounded-sm" data-testid="timed-op-recurrence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">One-time</SelectItem>
                  <SelectItem value="daily">Every day</SelectItem>
                  <SelectItem value="weekly">Every {weekday || "week"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="rounded-sm" data-testid="timed-op-time" />
          <p className="text-[10px] text-slate-400">Time is optional — linked operations keep their order and move with the MO if it's rescheduled.</p>
          <Button disabled={!sel || saving || (linked ? !moId : !date)} onClick={save} className="w-full rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="timed-op-save">
            {saving ? "Saving…" : "Add to Schedule"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Op chip with a popover to move (unlinks) or delete the operation.
function OpChip({ op, dayKey, moLabel, onChanged }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(dayKey);
  const [t, setT] = useState(op.time || "08:00");
  const move = async () => {
    try {
      await api.put(`/timed-operations/${op.id}`, { ...op, date: d, time: t, mo_id: "", position: "", recurrence: op.mo_id ? "none" : op.recurrence });
      toast.success(`${op.name} moved`);
      setOpen(false); onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const del = async () => {
    try { await api.delete(`/timed-operations/${op.id}`); toast.success(`${op.name} removed`); setOpen(false); onChanged?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="block w-full truncate rounded-sm border border-teal-200 bg-teal-50 px-1.5 py-1 text-left text-[10px] font-semibold leading-tight text-teal-700 hover:opacity-80"
          title={`${op.name} · ${fmtDur(op.duration_hours)}${moLabel ? ` · ${op.position} ${moLabel}` : op.recurrence !== "none" ? ` · recurring (${op.recurrence})` : ""}`}
          data-testid={`calendar-op-${op.id}-${dayKey}`}>
          <Timer className="mr-0.5 inline h-2.5 w-2.5" />{op.time ? `${fmtTime(op.time)} ` : ""}{op.name} · {fmtDur(op.duration_hours)}{moLabel ? <span className="font-normal"> ({op.position} {moLabel})</span> : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 rounded-sm p-3" data-testid={`op-edit-panel-${op.id}`}>
        <p className="mb-1 text-xs font-semibold text-slate-700">{op.name} · {fmtDur(op.duration_hours)}</p>
        {moLabel && <p className="mb-2 text-[10px] text-slate-500">Linked {op.position} {moLabel} — moving it converts it to a fixed date.</p>}
        <div className="grid grid-cols-2 gap-1.5">
          <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="h-8 rounded-sm text-xs" data-testid={`op-move-date-${op.id}`} />
          <Input type="time" value={t} onChange={(e) => setT(e.target.value)} className="h-8 rounded-sm text-xs" data-testid={`op-move-time-${op.id}`} />
        </div>
        <div className="mt-2 flex gap-1.5">
          <Button size="sm" onClick={move} className="h-8 flex-1 rounded-sm bg-[#002FA7] text-xs text-white hover:bg-[#002080]" data-testid={`op-move-save-${op.id}`}>Move</Button>
          <Button size="sm" variant="outline" onClick={del} className="h-8 rounded-sm border-red-200 text-xs text-red-600 hover:bg-red-50" data-testid={`op-delete-${op.id}`}>Delete{op.recurrence !== "none" ? " series" : ""}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const CAL_STATUS = {
  "Awaiting Production": "bg-slate-100 text-slate-700 border-slate-200",
  "In Production": "bg-blue-100 text-blue-700 border-blue-200",
  "Partially Completed": "bg-amber-100 text-amber-700 border-amber-200",
  Completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

// Month-grid calendar of scheduled MOs.
function ScheduleCalendar({ mos, ops = [], onOpen, onOpsChanged }) {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const todayKey = new Date().toLocaleDateString("en-CA");
  const byDate = {};
  const byId = {};
  mos.forEach((m) => {
    byId[m.id] = m;
    if (m.production_date) (byDate[m.production_date] = byDate[m.production_date] || []).push(m);
  });
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  const cells = Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  const monthLabel = month.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  return (
    <div className="border border-slate-200" data-testid="schedule-calendar">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="rounded-sm border border-slate-200 bg-white px-2 py-1 text-sm text-slate-600 hover:text-[#002FA7]" data-testid="calendar-prev">‹</button>
        <div className="flex items-center gap-3">
          <span className="font-heading text-sm font-bold uppercase tracking-[0.08em] text-slate-700" data-testid="calendar-month-label">{monthLabel}</span>
          <button onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }} className="rounded-sm border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-500 hover:text-[#002FA7]" data-testid="calendar-today">Today</button>
        </div>
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="rounded-sm border border-slate-200 bg-white px-2 py-1 text-sm text-slate-600 hover:text-[#002FA7]" data-testid="calendar-next">›</button>
      </div>
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (<div key={d} className="py-1.5">{d}</div>))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const key = d.toLocaleDateString("en-CA");
          const inMonth = d.getMonth() === month.getMonth();
          const isToday = key === todayKey;
          const dayMos = byDate[key] || [];
          const linkedHere = (op) => op.mo_id && byId[op.mo_id] && (byId[op.mo_id].production_date || "") === key;
          const dayOps = ops.filter((op) => (op.mo_id ? linkedHere(op) : opOccursOn(op, key)));
          const opsFor = (mid, pos) => dayOps.filter((op) => op.mo_id === mid && (pos === "before" ? op.position === "before" : op.position !== "before"));
          return (
            <div key={key} className={`min-h-[92px] border-b border-r border-slate-100 p-1.5 ${inMonth ? "bg-white" : "bg-slate-50/60"}`} data-testid={`calendar-day-${key}`}>
              <div className={`mb-1 flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${isToday ? "bg-[#002FA7] font-bold text-white" : inMonth ? "text-slate-600" : "text-slate-300"}`}>{d.getDate()}</div>
              <div className="space-y-1">
                {dayOps.filter((op) => !op.mo_id).map((op) => (
                  <OpChip key={`${op.id}-${key}`} op={op} dayKey={key} moLabel="" onChanged={onOpsChanged} />
                ))}
                {dayMos.map((m) => (
                  <div key={m.id} className="space-y-1">
                    {opsFor(m.id, "before").map((op) => (
                      <OpChip key={op.id} op={op} dayKey={key} moLabel={moNum(m.mwo_number)} onChanged={onOpsChanged} />
                    ))}
                    <button onClick={() => onOpen(m.id)}
                      className={`block w-full truncate rounded-sm border px-1.5 py-1 text-left text-[10px] font-semibold leading-tight hover:opacity-80 ${CAL_STATUS[m.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}
                      title={`${moNum(m.mwo_number)} · ${m.product_name || ""}${m.design_name ? ` · ${m.design_name}` : ""} · ${m.status || ""}`}
                      data-testid={`calendar-mo-${m.id}`}>
                      {moNum(m.mwo_number)}<span className="ml-1 font-normal">{m.product_name || ""}</span>
                    </button>
                    {opsFor(m.id, "after").map((op) => (
                      <OpChip key={op.id} op={op} dayKey={key} moLabel={moNum(m.mwo_number)} onChanged={onOpsChanged} />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const statusBadge = (status) => {
  const map = {
    "Awaiting Production": "bg-slate-100 text-slate-700",
    "In Production": "bg-blue-100 text-blue-700",
    "Partially Completed": "bg-amber-100 text-amber-700",
    "Completed": "bg-emerald-100 text-emerald-700",
  };
  return map[status] || "bg-slate-100 text-slate-700";
};

const ProgressBar = ({ produced, ordered, pct }) => {
  const over = pct > 100;
  return (
    <div className="w-full min-w-[130px]">
      <div className={`mb-1 text-xs ${over ? "font-semibold text-amber-600" : "text-slate-600"}`}>{Math.round(produced).toLocaleString()} / {Math.round(ordered).toLocaleString()} ({Math.round(pct)}%)</div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
        <div className={`h-full rounded-full transition-all ${over ? "bg-amber-500" : "bg-[#002FA7]"}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
};

export default function ProductionPlanning() {
  const [view, setView] = useState({ mode: "list" }); // list | new | view | edit
  const [reports, setReports] = useState([]);
  const [products, setProducts] = useState([]);
  const [labels, setLabels] = useState([]);
  const [mos, setMos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [matTab, setMatTab] = useState("PET");
  const [fWater, setFWater] = useState("all");
  const [fSize, setFSize] = useState("all");
  const [detailMo, setDetailMo] = useState(null);
  const [showCreator, setShowCreator] = useState(false);
  const [toDeleteMo, setToDeleteMo] = useState(null);
  const [toReopenMo, setToReopenMo] = useState(null);
  const [bomMo, setBomMo] = useState(null);
  const [bomMovements, setBomMovements] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [rddOn, setRddOn] = useState(false);
  const [search, setSearch] = useState("");
  const [schedView, setSchedView] = useState("list");
  const [ops, setOps] = useState([]);
  const loadOps = useCallback(async () => {
    try {
      const { data } = await api.get("/timed-operations");
      setOps(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, []);
  useEffect(() => { loadOps(); }, [loadOps]);

  const companyRef = useRef(null);
  const [editMo, setEditMo] = useState(null);
  const [soFromMo, setSoFromMo] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [coPackers, setCoPackers] = useState([]);
  const printPO = async (m) => {
    if (!companyRef.current) {
      try { companyRef.current = (await api.get("/company-settings")).data; } catch { companyRef.current = {}; }
    }
    printProductionOrder(m, companyRef.current);
  };

  const openBom = async (m) => {
    setBomMo(m);
    setBomMovements(null);
    try { const { data } = await api.get(`/manufacturing-orders/${m.id}/inventory-movements`); setBomMovements(data); }
    catch (e) { if (process.env.NODE_ENV !== "production") console.debug("inventory-movements unavailable", e); }
  };

  const loadReportsMos = useCallback(async () => {
    const [rep, mo] = await Promise.all([api.get("/production-reports"), api.get("/manufacturing-orders")]);
    setReports(rep.data);
    setMos(mo.data);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rep, prod, lab, mo, cust, cps] = await Promise.all([
        api.get("/production-reports"), api.get("/products"), api.get("/labels"), api.get("/manufacturing-orders"),
        api.get("/customers").catch(() => ({ data: [] })),
        api.get("/co-packers").catch(() => ({ data: [] })),
      ]);
      setReports(rep.data);
      setProducts(prod.data);
      setLabels(lab.data);
      setMos(mo.data);
      setCustomers(cust.data);
      setCoPackers(cps.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteLabel = async (id) => {
    try { await api.delete(`/labels/${id}`); toast.success("Label deleted"); const { data } = await api.get("/labels"); setLabels(data); }
    catch (e) { toast.error("Failed to delete label"); }
  };

  const save = async (payload) => {
    setSaving(true);
    try {
      const effectToast = (data, base) => {
        const fx = data?.inventory_effect;
        if (fx && fx.cases > 0) {
          const where = fx.copacker_name ? ` at ${fx.copacker_name}` : " to finished goods";
          const mo = fx.mwo_number ? ` · ${fx.mwo_number} ${fx.mo_status}${fx.mo_completion != null ? ` (${fx.mo_completion}%)` : ""}` : "";
          const filled = (fx.filled_mos || []).length
            ? ` · filled ${fx.filled_mos.map((a) => `${moNum(a.mwo_number)} (+${Number(a.bottles).toLocaleString()} btl)`).join(", ")}`
            : "";
          toast.success(`${base} — ${Number(fx.cases).toLocaleString()} cases added${where}${mo}${filled}`);
        } else toast.success(base);
      };
      if (view.mode === "edit") { const { data } = await api.put(`/production-reports/${view.report.id}`, payload); effectToast(data, "Report updated"); }
      else { const { data } = await api.post("/production-reports", payload); effectToast(data, "Report saved"); }
      setView({ mode: "list" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save report");
    } finally { setSaving(false); }
  };

  const openView = async (id) => {
    try { const { data } = await api.get(`/production-reports/${id}`); setDetailMo(null); setView({ mode: "view", report: data }); }
    catch (e) { toast.error("Failed to open report"); }
  };
  const openEdit = async (id) => {
    try { const { data } = await api.get(`/production-reports/${id}`); setView({ mode: "edit", report: data }); }
    catch (e) { toast.error("Failed to open report"); }
  };
  const openDetail = async (id) => {
    try { const { data } = await api.get(`/manufacturing-orders/${id}`); setDetailMo(data); }
    catch (e) { toast.error("Failed to open order"); }
  };
  const doDelete = async () => {
    if (!toDelete) return;
    try { await api.delete(`/production-reports/${toDelete.id}`); toast.success("Report deleted"); await load(); }
    catch (e) { toast.error("Failed to delete report"); }
    finally { setToDelete(null); }
  };

  const deleteReport = async (id) => {
    try {
      await api.delete(`/production-reports/${id}`);
      toast.success("Report deleted");
      await load();
      if (detailMo) { const { data } = await api.get(`/manufacturing-orders/${detailMo.id}`); setDetailMo(data); }
    } catch (e) { toast.error("Failed to delete report"); }
  };

  const doDeleteMo = async () => {
    if (!toDeleteMo) return;
    try {
      const { data } = await api.delete(`/manufacturing-orders/${toDeleteMo.id}`);
      const n = data?.reports_removed || 0;
      toast.success(`${moNum(toDeleteMo.mwo_number)} deleted${n ? ` · ${n} report(s) reversed` : ""}`);
      setDetailMo(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete order");
    } finally { setToDeleteMo(null); }
  };

  const doReopenMo = async () => {
    if (!toReopenMo) return;
    try {
      const { data } = await api.post(`/manufacturing-orders/${toReopenMo.id}/reopen`);
      const n = data?.reports_removed || 0;
      toast.success(`${moNum(toReopenMo.mwo_number)} reopened${n ? ` · ${n} report(s) reversed` : ""} — back to Awaiting Production`);
      setDetailMo(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reopen order");
    } finally { setToReopenMo(null); }
  };

  const enterProduction = (m) => {
    const oz = (m.bottle_size || "").match(/([\d.]+)\s*oz/i)?.[1];
    const cpp = m.bottles_per_pallet && m.case_pack ? String(Math.round((m.bottles_per_pallet / m.case_pack) * 100) / 100) : "";
    const prod = products.find((p) => p.id === m.product_id);
    const labelLine = (prod?.lines || []).find((l) => /label/i.test(l.component_name || ""));
    const design = (labelLine?.designs || []).find((d) => d.id === m.design_id);
    const capColor = design?.cap_name || "";
    const orderedBottles = m.ordered_bottles || (m.unit === "truckloads"
      ? (m.quantity || 0) * (m.pallets_per_truckload || 0) * (m.bottles_per_pallet || 0)
      : (m.quantity || 0) * (m.bottles_per_pallet || 0));
    const prefill = {
      date: m.production_date || "",
      line_items: [{
        label_name: m.design_name || "",
        bottle_size: OZ_TO_LABEL[oz] || "",
        pack_size: m.case_pack ?? null,
        cases_per_pallet: cpp,
        cap_color: capColor,
        po_number: m.mwo_number || "",
        po_qty: orderedBottles ? String(Math.round(orderedBottles)) : "",
        po_qty_unit: "bottles",
      }],
    };
    setDetailMo(null);
    setView({ mode: "new", prefill });
  };

  // ----- Report form view -----
  if (view.mode === "new" || view.mode === "view" || view.mode === "edit") {
    const isView = view.mode === "view";
    const isNew = view.mode === "new";
    const title = isNew ? "New Production Report" : isView ? "Production Report" : "Edit Production Report";
    return (
      <main className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 md:px-10 md:py-12" data-testid="production-planning-page">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <button onClick={() => setView({ mode: "list" })} className="mb-2 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-schedule">
              <ArrowLeft className="h-4 w-4" /> Production Queue
            </button>
            <h1 className="font-heading text-2xl font-black tracking-tighter text-slate-900 sm:text-3xl">{title}</h1>
          </div>
          {isView && (
            <Button className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" onClick={() => setView({ mode: "edit", report: view.report })} data-testid="edit-report-button">
              <Pencil className="mr-1 h-4 w-4" /> Edit
            </Button>
          )}
        </div>
        <ProductionReportForm
          initial={isNew ? (view.prefill || null) : view.report}
          readOnly={isView}
          mos={mos}
          coPackers={coPackers}
          saving={saving}
          products={products}
          labels={labels}
          onDeleteLabel={deleteLabel}
          onSave={save}
          onCancel={() => setView({ mode: "list" })}
        />
      </main>
    );
  }

  // ----- Schedule categorization -----
  const today = todayStr();
  const isAlumMo = (m) => /alum/i.test(m.product_name || "");
  const matMatch = (m) => (matTab === "ALUM" ? isAlumMo(m) : !isAlumMo(m));
  const placed = mos.filter((m) => m.placed && matMatch(m));
  const designMap = buildDesignMap(products);
  const rddRiskIds = new Set();
  const seq = (arr) => {
    if (!rddOn) return sequenceMos(arr, designMap);
    const { list, riskIds } = sequenceWithRdd(arr, designMap, today);
    riskIds.forEach((id) => rddRiskIds.add(id));
    return list;
  };
  const all = seq(placed.filter((m) => !m.projected));
  const queuePos = new Map(all.map((m, i) => [m.id, i + 1]));
  const matchesStatus = (m) =>
    statusFilter === "all" ? true
    : statusFilter === "in_production" ? m.status === "In Production" || m.status === "Partially Completed"
    : statusFilter === "awaiting" ? m.status === "Awaiting Production"
    : m.status === "Completed";
  const q = search.trim().toLowerCase();
  const SIZE_RE = { "12": /12\s*oz|355\s*ml/i, "16.9": /16\.9\s*oz|500\s*ml/i, "23.7": /23\.7\s*oz|700\s*ml/i, "1l": /33\.8\s*oz|1\s*l\b/i };
  const filterMo = (m) => matchesStatus(m)
    && matMatch(m)
    && (!q || moNum(m.mwo_number).toLowerCase().includes(q))
    && (fWater === "all" || moWater(m, designMap) === fWater)
    && (fSize === "all" || SIZE_RE[fSize].test(m.product_name || ""));
  const rows = all.filter(filterMo);
  const hasFilters = statusFilter !== "all" || !!q;

  const isOverdue = (m) => m.production_date && m.production_date < today && m.status !== "Completed";

  const renderAction = (m) => {
    if (m.status === "Completed") {
      const ids = m.linked_report_ids || [];
      if (ids.length > 1) {
        return <Button size="sm" variant="outline" className="rounded-sm" onClick={() => openDetail(m.id)} data-testid={`view-history-${m.id}`}>View Production History</Button>;
      }
      return <Button size="sm" variant="outline" className="rounded-sm" onClick={() => (ids[0] ? openView(ids[0]) : openDetail(m.id))} data-testid={`view-report-${m.id}`}>View Report</Button>;
    }
    return <Button size="sm" className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" onClick={() => enterProduction(m)} data-testid={`enter-production-${m.id}`}>Enter Production</Button>;
  };

  return (
    <main className="px-4 py-8 sm:px-6 md:px-10 md:py-12" data-testid="production-planning-page">
      <div className="mx-auto max-w-[1400px]">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Operations</p>
      <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">Manufacturing Orders</h1>

      <DailyReportsSummary reports={reports} mos={mos} onView={openView} onEdit={openEdit} onDelete={setToDelete} onNew={() => setView({ mode: "new" })} />

      <ProjectionsSection products={products} customers={customers} onConverted={loadReportsMos} />

      {/* Manufacturing Order documents */}
      <div className="mt-14 border-t-2 border-slate-200 pt-8" data-testid="mo-documents-section">
        <div className="flex items-center gap-2">
          <ListTree className="h-5 w-5 text-[#002FA7]" />
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Manufacturing Orders — Documents</h2>
        </div>
        <p className="mt-1 text-sm text-slate-500">Each order is a Production Order document; the daily reports above record what was actually produced against it.</p>
      </div>

      <div className="mt-6 flex border-b border-slate-200" data-testid="mo-material-tabs">
        {["PET", "ALUM"].map((m) => {
          const count = mos.filter((x) => x.placed && (m === "ALUM" ? /alum/i.test(x.product_name || "") : !/alum/i.test(x.product_name || ""))).length;
          return (
            <button key={m} onClick={() => setMatTab(m)}
              className={`-mb-px border-b-2 px-6 py-2.5 font-heading text-sm font-bold uppercase tracking-[0.08em] transition-colors ${matTab === m ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-400 hover:text-slate-700"}`}
              data-testid={`mo-tab-${m.toLowerCase()}`}>
              {m} Production <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${matTab === m ? "bg-[#002FA7]/10 text-[#002FA7]" : "bg-slate-100 text-slate-400"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      </div>

      {/* Production Schedule */}
      <div className="mt-10" data-testid="production-schedule">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-[#002FA7]" />
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{schedView === "calendar" ? "RDD Calendar" : "Production Queue"}</h2>
          </div>
          <div className="flex items-center gap-2">
            <TimedOpPopover mos={mos} onCreated={() => { loadOps(); setSchedView("calendar"); }} />
            <Button onClick={() => setShowCreator((v) => !v)} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="toggle-create-mwo">
              <Plus className="mr-1 h-4 w-4" /> Create Manufacturing Order
              <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${showCreator ? "rotate-180" : ""}`} />
            </Button>
          </div>
        </div>

        {showCreator && (
          <div className="mb-6 rounded-sm border border-slate-200 bg-white p-5 shadow-sm" data-testid="create-mwo-panel">
            <MOCreator onPlaced={loadReportsMos} onOpenDetail={openDetail} onFinished={() => setShowCreator(false)} />
          </div>
        )}

        <div className="mb-5 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid="schedule-view-toggle">
            {[["list", "Production Queue"], ["calendar", "RDD Calendar"]].map(([key, label]) => (
              <button key={key} onClick={() => setSchedView(key)}
                className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${schedView === key ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                data-testid={`schedule-view-${key}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid="schedule-status-filter">
            {[["all", "All"], ["awaiting", "Awaiting Production"], ["in_production", "In Production"], ["completed", "Completed"]].map(([key, label]) => (
              <button key={key} onClick={() => setStatusFilter(key)}
                className={`rounded-sm px-3 py-1.5 text-xs font-semibold transition-colors ${statusFilter === key ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                data-testid={`status-filter-${key}`}>
                {label} <span className="ml-1 text-[10px] font-normal text-slate-400">{all.filter((m) => (key === "all" ? true : key === "in_production" ? m.status === "In Production" || m.status === "Partially Completed" : key === "awaiting" ? m.status === "Awaiting Production" : m.status === "Completed")).length}</span>
              </button>
            ))}
          </div>
          {schedView === "list" && (
            <>
              <Select value={fWater} onValueChange={setFWater}>
                <SelectTrigger className="h-9 w-[130px] rounded-sm text-xs" data-testid="filter-water"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Water Type</SelectItem>
                  <SelectItem value="Natural Spring">Natural Spring</SelectItem>
                  <SelectItem value="Alkaline">Alkaline</SelectItem>
                  <SelectItem value="Electrolyte">Electrolyte</SelectItem>
                </SelectContent>
              </Select>
              <Select value={fSize} onValueChange={setFSize}>
                <SelectTrigger className="h-9 w-[120px] rounded-sm text-xs" data-testid="filter-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Bottle Size</SelectItem>
                  <SelectItem value="12">12 oz / 355 mL</SelectItem>
                  <SelectItem value="16.9">16.9 oz / 500 mL</SelectItem>
                  <SelectItem value="23.7">23.7 oz / 700 mL</SelectItem>
                  <SelectItem value="1l">1 L / 33.8 oz</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search MO #"
              className="h-9 w-52 rounded-sm border-slate-200 pl-8 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-[#002FA7]" data-testid="mwo-search-input" />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="mwo-search-clear">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 rounded-sm border border-slate-200 bg-slate-50 px-3 py-1.5"
            title={rddOn ? "Optimize production while ensuring orders are produced at least 1 day before their Requested Delivery Date." : "Sequence production for operational efficiency only."}>
            <Switch checked={rddOn} onCheckedChange={setRddOn} data-testid="rdd-constraint-toggle" />
            <div className="leading-tight">
              <p className="text-xs font-semibold text-slate-700">Include RDD Constraint</p>
              <p className="text-[10px] text-slate-400">{rddOn ? "ON — RDD Constrained" : "OFF — Production Optimized"}</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#002FA7]" /></div>
        ) : schedView === "calendar" ? (
          <ScheduleCalendar mos={mos.filter(filterMo)} ops={ops} onOpen={openDetail} onOpsChanged={loadOps} />
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center border border-dashed border-slate-300 px-6 py-16 text-center" data-testid="schedule-empty">
            <CalendarDays className="h-9 w-9 text-slate-300" strokeWidth={1.5} />
            {hasFilters ? (
              <>
                <p className="mt-3 text-sm text-slate-500">No manufacturing orders match your filters.</p>
                <button onClick={() => { setStatusFilter("all"); setSearch(""); }} className="mt-2 text-sm font-semibold text-[#002FA7] hover:underline" data-testid="clear-schedule-filters">Clear filters</button>
              </>
            ) : (
              <p className="mt-3 text-sm text-slate-500">No manufacturing orders in the queue. Use the <b>Create Manufacturing Order</b> button to add production.</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200" data-testid="schedule-table">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                  <th className="px-3 py-3">MO # / PO Doc</th>
                  <th className="border-l border-slate-200 px-3 py-3">#</th>
                  <th className="border-l border-slate-200 px-3 py-3">RDD</th>
                  <th className="border-l border-slate-200 px-3 py-3">Product / Design</th>
                  <th className="border-l border-slate-200 px-3 py-3">Water Type</th>
                  <th className="border-l border-slate-200 px-3 py-3">Cap Color</th>
                  <th className="border-l border-slate-200 px-3 py-3">Pack</th>
                  <th className="border-l border-slate-200 px-3 py-3">Ordered</th>
                  <th className="border-l border-slate-200 px-3 py-3">Status</th>
                  <th className="border-l border-slate-200 px-3 py-3">Progress</th>
                  <th className="w-44 border-l border-slate-200 px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => {
                  const overdue = isOverdue(m);
                  const ordered = m.ordered_bottles || m.bottle_quantity || 0;
                  return (
                    <tr key={m.id} className={`border-b ${overdue ? "border-red-200 bg-red-50/60" : "border-slate-100 hover:bg-slate-50/60"}`} data-testid={`schedule-row-${m.id}`}>
                      <td className="px-3 py-3">
                        <button className="whitespace-nowrap font-semibold text-[#002FA7] hover:underline" onClick={() => openDetail(m.id)} data-testid={`mwo-link-${m.id}`}>{moNum(m.mwo_number)}</button>
                        <div className="mt-0.5">
                          {m.source_so_number ? (
                            <span className="inline-flex items-center rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700" data-testid={`mo-source-so-${m.id}`}>from {m.source_so_number}</span>
                          ) : (
                            <button onClick={() => setSoFromMo(m)} title="Create a linked Sales Order for this MO"
                              className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-400 hover:border-[#002FA7] hover:text-[#002FA7]"
                              data-testid={`create-so-${m.id}`}>
                              <Plus className="h-2.5 w-2.5" /> Create SO
                            </button>
                          )}
                        </div>
                        <Button variant="outline" size="sm" className="mt-1.5 h-7 rounded-sm border-slate-300 px-2 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]" title="Print the Production Order document" onClick={() => printPO(m)} data-testid={`print-po-${m.id}`}>
                          <FileText className="mr-1 h-3.5 w-3.5" /> PO Doc
                        </Button>
                      </td>
                      <td className="border-l border-slate-100 px-3 py-3 font-heading text-sm font-bold tabular-nums text-slate-400" data-testid={`queue-pos-${m.id}`}>{queuePos.get(m.id)}</td>
                      <td className="border-l border-slate-100 px-3 py-3 text-slate-600">
                        <ScheduleDateCell m={m} onSaved={loadReportsMos} />
                        {rddOn && rddRiskIds.has(m.id) && m.status !== "Completed" && (
                          <div className="mt-1 inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700" data-testid={`rdd-risk-${m.id}`}>
                            At Risk — Cannot Meet RDD
                          </div>
                        )}
                      </td>
                      <td className="border-l border-slate-100 px-3 py-3">
                        <div className="text-slate-800">{m.product_name}</div>
                        <div className="text-xs text-slate-500">{m.design_name || "—"}</div>
                      </td>
                      <td className="border-l border-slate-100 px-3 py-3 text-slate-600 whitespace-nowrap" data-testid={`water-type-${m.id}`}>{moWater(m, designMap) || "—"}</td>
                      <td className="border-l border-slate-100 px-3 py-3 text-slate-600 whitespace-nowrap" data-testid={`cap-color-${m.id}`}>{moCap(m, designMap) || "—"}</td>
                      <td className="border-l border-slate-100 px-3 py-3 text-slate-600">{m.case_pack}-Pack</td>
                      <td className="border-l border-slate-100 px-3 py-3 text-slate-600 whitespace-nowrap" data-testid={`ordered-cell-${m.id}`}>
                        {(() => {
                          const fmtQ = (n) => Number(Math.round(n * 100) / 100).toLocaleString();
                          const cp = Number(m.case_pack) || 0;
                          const bpp = Number(m.bottles_per_pallet) || 0;
                          const ppt = Number(m.pallets_per_truckload) || 0;
                          const cases = cp ? ordered / cp : null;
                          const pallets = bpp ? ordered / bpp : null;
                          const trucks = ppt && pallets != null ? pallets / ppt : null;
                          const top = [trucks != null ? `${fmtQ(trucks)} Truckload${trucks === 1 ? "" : "s"}` : null, pallets != null ? `${fmtQ(pallets)} Pallet${pallets === 1 ? "" : "s"}` : null].filter(Boolean).join(" · ");
                          const bottom = [cases != null ? `${fmtQ(cases)} Case${cases === 1 ? "" : "s"}` : null, `${Math.round(ordered).toLocaleString()} Bottles`].filter(Boolean).join(" · ");
                          return (
                            <>
                              {top && <div className="text-xs text-slate-500">{top}</div>}
                              <div>{bottom}</div>
                            </>
                          );
                        })()}
                      </td>
                      <td className="border-l border-slate-100 px-3 py-3">
                        <span className={`rounded-sm px-2 py-1 text-xs font-medium ${statusBadge(m.status)}`}>{m.status}</span>
                        {m.is_overage && <span className="ml-1.5 rounded-sm bg-amber-100 px-2 py-1 text-xs font-bold uppercase tracking-wide text-amber-700" title="Auto-created for production beyond open MOs — traces the excess to its report" data-testid={`overage-${m.id}`}>Overage</span>}
                        {overdue && <span className="ml-1.5 rounded-sm bg-red-100 px-2 py-1 text-xs font-bold uppercase tracking-wide text-red-700" data-testid={`overdue-${m.id}`}>Overdue</span>}
                      </td>
                      <td className="border-l border-slate-100 px-3 py-3">
                        <ProgressBar produced={m.produced_bottles || 0} ordered={ordered} pct={m.completion || 0} />
                      </td>
                      <td className="border-l border-slate-100 px-3 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {renderAction(m)}
                          {m.status !== "Awaiting Production" && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-amber-600" title="Reopen order (revert to Awaiting Production)" onClick={() => setToReopenMo(m)} data-testid={`reopen-mo-${m.id}`}>
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#002FA7]" title="Edit order details" onClick={() => setEditMo(m)} data-testid={`edit-mo-${m.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#002FA7]" title="Order BOM" onClick={() => openBom(m)} data-testid={`view-bom-${m.id}`}>
                            <ListTree className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" title="Delete order" onClick={() => setToDeleteMo(m)} data-testid={`delete-mo-${m.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Other reports section removed — all reports now appear in the Daily Production Reports summary above. */}

      <MODetailModal detail={detailMo} reports={reports} onClose={() => setDetailMo(null)} onViewReport={openView} onDeleteReport={deleteReport} />
      <EditMODialog mo={editMo} products={products} open={!!editMo} onOpenChange={(o) => { if (!o) setEditMo(null); }} onSaved={loadReportsMos} />
      <SODialog
        open={!!soFromMo}
        onOpenChange={(o) => { if (!o) setSoFromMo(null); }}
        customers={customers}
        products={products.filter((p) => (p.pack_configs || []).length)}
        initial={soFromMo ? {
          customer_id: soFromMo.customer_id || "",
          ship_date: soFromMo.production_date || "",
          lines: [{
            id: "", product_id: soFromMo.product_id, config_id: soFromMo.config_id || "", design_id: soFromMo.design_id || "",
            quantity: soFromMo.case_pack ? Math.round(((soFromMo.bottle_quantity || 0) / soFromMo.case_pack) * 100) / 100 : "",
            unit: "cases",
          }],
        } : null}
        onSaved={async (so) => {
          const m = soFromMo;
          try {
            const ln = (so?.lines || [])[0];
            if (m && ln) {
              await api.put(`/sales-orders/${so.id}/line-mo`, { line_id: ln.id, mo_id: m.id, mo_number: m.mwo_number });
              await api.put(`/manufacturing-orders/${m.id}/source-so`, { source_so_id: so.id, source_so_number: so.so_number });
              toast.success(`${so.so_number} linked to ${moNum(m.mwo_number)}`);
            }
          } catch {
            toast.error("Sales order created, but linking to the MO failed — check the Sales Orders page");
          }
          setSoFromMo(null);
          loadReportsMos();
        }}
      />

      <Dialog open={!!bomMo} onOpenChange={(o) => { if (!o) { setBomMo(null); setBomMovements(null); } }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-lg" data-testid="order-bom-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{moNum(bomMo?.mwo_number)} · Order BOM</DialogTitle>
            <DialogDescription className="text-left">
              {bomMo?.product_name}{bomMo?.design_name ? ` · ${bomMo.design_name}` : ""} · {bomMo?.case_pack}-Pack · {Math.round(bomMo?.ordered_bottles || bomMo?.bottle_quantity || 0).toLocaleString()} bottles
            </DialogDescription>
          </DialogHeader>
          {(bomMo?.bom_snapshot || []).length === 0 ? (
            <p className="py-4 text-sm text-slate-400">No BOM snapshot recorded for this order.</p>
          ) : (
            <table className="w-full table-fixed border-collapse text-xs">
              <thead>
                <tr className="text-[9px] uppercase text-slate-500">
                  <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left">Component</th>
                  <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left">Type</th>
                  <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-right">Qty</th>
                </tr>
              </thead>
              <tbody>
                {(bomMo?.bom_snapshot || []).map((b, i) => (
                  <tr key={`${b.name}-${b.type || ""}-${i}`} data-testid={`order-bom-row-${i}`}>
                    <td className="border border-slate-300 px-2 py-1 font-semibold text-slate-800">{b.name}</td>
                    <td className="border border-slate-300 px-2 py-1 text-slate-500">{b.type || "—"}</td>
                    <td className="border border-slate-300 px-2 py-1 text-right text-slate-600">{b.display}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {bomMovements && (bomMovements.components_out.length > 0 || bomMovements.finished_goods_in.length > 0) && (
            <div className="mt-4 space-y-4 border-t border-slate-200 pt-4" data-testid="bom-movements">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Actual production movements</p>

              <div>
                <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-red-600"><ArrowDownRight className="h-3.5 w-3.5" /> Taken from Component Inventory</p>
                {bomMovements.components_out.length === 0 ? (
                  <p className="text-xs text-slate-400">No components consumed yet.</p>
                ) : (
                  <table className="w-full table-fixed border-collapse text-xs">
                    <thead>
                      <tr className="text-[9px] uppercase text-slate-500">
                        <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left">Component</th>
                        <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left">Type</th>
                        <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-right">Consumed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bomMovements.components_out.map((c, i) => (
                        <tr key={`${c.name}-${c.type || ""}-${i}`} data-testid={`bom-comp-out-${i}`}>
                          <td className="border border-slate-300 px-2 py-1 font-semibold text-slate-800">{c.name}</td>
                          <td className="border border-slate-300 px-2 py-1 text-slate-500">{c.type || "—"}</td>
                          <td className="border border-slate-300 px-2 py-1 text-right font-medium text-red-600">-{Math.round(c.qty).toLocaleString()}{c.unit === "volume" ? " (vol)" : ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div>
                <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-emerald-600"><ArrowUpRight className="h-3.5 w-3.5" /> Added to Finished Goods Inventory</p>
                {bomMovements.finished_goods_in.length === 0 ? (
                  <p className="text-xs text-slate-400">No finished goods produced yet.</p>
                ) : (
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="text-[9px] uppercase text-slate-500">
                        <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left">SKU</th>
                        <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left">Finished Good</th>
                        <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-right">Cases</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bomMovements.finished_goods_in.map((f, i) => (
                        <tr key={`${f.sku}-${i}`} data-testid={`bom-fg-in-${i}`}>
                          <td className="border border-slate-300 px-2 py-1 font-semibold text-[#002FA7]">{f.sku}</td>
                          <td className="border border-slate-300 px-2 py-1 text-slate-700">{f.product_name}{f.design_name ? ` · ${f.design_name}` : ""} · {f.case_pack}-Pack</td>
                          <td className="border border-slate-300 px-2 py-1 text-right font-medium text-emerald-600">+{Math.round(f.cases).toLocaleString()}{f.remainder ? ` (+${f.remainder} loose)` : ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDeleteMo} onOpenChange={(o) => !o && setToDeleteMo(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Delete {moNum(toDeleteMo?.mwo_number)}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the manufacturing order{(toDeleteMo?.linked_report_ids || []).length > 0
                ? ` and its ${(toDeleteMo.linked_report_ids || []).length} linked production report(s). All inventory movements from those reports (components consumed and finished goods produced) will be reversed.`
                : ". No production reports are linked, so no inventory changes."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="cancel-delete-mo">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={doDeleteMo} data-testid="confirm-delete-mo">Delete Order</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toReopenMo} onOpenChange={(o) => !o && setToReopenMo(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Reopen {moNum(toReopenMo?.mwo_number)}?</AlertDialogTitle>
            <AlertDialogDescription>
              This reverts the order to <b>Awaiting Production</b> without deleting it.{(toReopenMo?.linked_report_ids || []).length > 0
                ? ` Its ${(toReopenMo.linked_report_ids || []).length} linked production report(s) will be removed and all their inventory movements reversed — consumed components return to stock and produced finished goods are taken back out.`
                : " No production reports are linked, so no inventory changes."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="cancel-reopen-mo">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-amber-600 hover:bg-amber-700" onClick={doReopenMo} data-testid="confirm-reopen-mo">Reopen Order</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Delete report?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the production report from history.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="cancel-delete-report">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="confirm-delete-report">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
